import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { duplicateTrigger } from '../application/service/apiService';

const useDuplication = (errorManagerRef, fetchData, currentPage) => {
  const { acontID, token } = useParams();
  const { t } = useTranslation();
  
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateWabaModalOpen, setDuplicateWabaModalOpen] = useState(false);
  const [duplicateBrokerModalOpen, setDuplicateBrokerModalOpen] = useState(false);
  const [duplicateDateTime, setDuplicateDateTime] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [selectedTriggerId, setSelectedTriggerId] = useState(null);
  const [wabaTemplates, setWabaTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [brokerMensagem, setBrokerMensagem] = useState('');
  const [brokerMensagemRapida, setBrokerMensagemRapida] = useState('');
  const [brokerMensagensRapidas, setBrokerMensagensRapidas] = useState([]);
  const [brokerImagem, setBrokerImagem] = useState(null);
  const [brokerImagemUrl, setBrokerImagemUrl] = useState('');
  const [wabaTemplateCurrentPage, setWabaTemplateCurrentPage] = useState(1);
  const [wabaTemplateLastPage, setWabaTemplateLastPage] = useState(1);
  const [brokerTemplateCurrentPage, setBrokerTemplateCurrentPage] = useState(1);
  const [brokerTemplateLastPage, setBrokerTemplateLastPage] = useState(1);
  const [isLoadingMoreWabaTemplates, setIsLoadingMoreWabaTemplates] = useState(false);
  const [isLoadingMoreBrokerTemplates, setIsLoadingMoreBrokerTemplates] = useState(false);
  const [wabaChannelId, setWabaChannelId] = useState(null);
  const [brokerChannelId, setBrokerChannelId] = useState(null);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const fetchWabaTemplates = async (channelId) => {
    try {
      
      // Carregar apenas a primeira página
      let firstUrl = `${process.env.REACT_APP_URL_DISP}/get-quick-messages/customer/${acontID}?channel_id=${channelId}&is_template=true&perPage=150&page=1`;
      const firstResponse = await fetch(firstUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!firstResponse.ok) throw new Error('Erro ao buscar templates');
      const firstData = await firstResponse.json();
      setWabaTemplates(firstData.data || []);
      setWabaTemplateCurrentPage(1);
      setWabaTemplateLastPage(firstData.last_page || 1);
    } catch (error) {
      errorManagerRef.current?.showError(t('duplication.error.wabaTemplates'));
    }
  };

  const loadMoreWabaTemplates = async (channelId) => {
    if (isLoadingMoreWabaTemplates || wabaTemplateCurrentPage >= wabaTemplateLastPage) {
      return;
    }

    setIsLoadingMoreWabaTemplates(true);
    try {
      
      const nextPage = wabaTemplateCurrentPage + 1;
      let url = `${process.env.REACT_APP_URL_DISP}/get-quick-messages/customer/${acontID}?channel_id=${channelId}&is_template=true&perPage=150&page=${nextPage}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao buscar templates');
      const data = await response.json();
      setWabaTemplates((prev) => [...prev, ...(data.data || [])]);
      setWabaTemplateCurrentPage(nextPage);
    } catch (error) {
      errorManagerRef.current?.showError(t('duplication.error.loadMoreWaba'));
    } finally {
      setIsLoadingMoreWabaTemplates(false);
    }
  };

  const fetchBrokerTemplates = async (channelId) => {
    try {
      
      // Carregar apenas a primeira página
      let firstUrl = `${process.env.REACT_APP_URL_DISP}/get-quick-messages/customer/${acontID}?channel_id=${channelId}&is_template=false&perPage=150&page=1`;
      const firstResponse = await fetch(firstUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!firstResponse.ok) throw new Error('Erro ao buscar mensagens rápidas');
      const firstData = await firstResponse.json();
      setBrokerMensagensRapidas(firstData.data || []);
      setBrokerTemplateCurrentPage(1);
      setBrokerTemplateLastPage(firstData.last_page || 1);
    } catch (error) {
      errorManagerRef.current?.showError(t('duplication.error.brokerMessages'));
    }
  };

  const loadMoreBrokerTemplates = async (channelId) => {
    if (isLoadingMoreBrokerTemplates || brokerTemplateCurrentPage >= brokerTemplateLastPage) {
      return;
    }

    setIsLoadingMoreBrokerTemplates(true);
    try {
      
      const nextPage = brokerTemplateCurrentPage + 1;
      let url = `${process.env.REACT_APP_URL_DISP}/get-quick-messages/customer/${acontID}?channel_id=${channelId}&is_template=false&perPage=150&page=${nextPage}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao buscar mensagens rápidas');
      const data = await response.json();
      setBrokerMensagensRapidas((prev) => [...prev, ...(data.data || [])]);
      setBrokerTemplateCurrentPage(nextPage);
    } catch (error) {
      errorManagerRef.current?.showError(t('duplication.error.loadMoreBroker'));
    } finally {
      setIsLoadingMoreBrokerTemplates(false);
    }
  };

  const handleDuplicateClick = (triggerId, selectedRow) => {
    setSelectedTriggerId(triggerId);
    const now = new Date();
    setDuplicateDateTime(formatDate(now));
    
    if (selectedRow?.trigger_type === 'WABA') {
      setWabaChannelId(selectedRow.channel_id);
      fetchWabaTemplates(selectedRow.channel_id);
      setSelectedTemplate(selectedRow.template_tag || '');
      setDuplicateWabaModalOpen(true);
    } else if (selectedRow?.trigger_type === 'BROKER') {
      setBrokerChannelId(selectedRow.channel_id);
      fetchBrokerTemplates(selectedRow.channel_id);
      setBrokerMensagem('');
      setBrokerMensagemRapida('');
      setBrokerImagem(null);
      setBrokerImagemUrl('');
      setDuplicateBrokerModalOpen(true);
    } else {
      setDuplicateModalOpen(true);
    }
  };

  const handleDuplicateModalClose = (type) => {
    if (type === 'waba') {
      setDuplicateWabaModalOpen(false);
      setSelectedTemplate('');
      setWabaTemplates([]);
    } else if (type === 'broker') {
      setDuplicateBrokerModalOpen(false);
      setBrokerMensagem('');
      setBrokerMensagemRapida('');
      setBrokerMensagensRapidas([]);
      setBrokerImagem(null);
      setBrokerImagemUrl('');
    } else {
      setDuplicateModalOpen(false);
    }
    setSelectedTriggerId(null);
    setDuplicateDateTime('');
  };

  const handleConfirmDuplicate = async () => {
    setIsDuplicating(true);
    try {
      await duplicateTrigger(acontID, selectedTriggerId, duplicateDateTime, token);
      setDuplicateModalOpen(false);
      setSelectedTriggerId(null);
      setDuplicateDateTime('');
      fetchData(currentPage);
      errorManagerRef.current.showError(t('duplication.success.duplicated'), 'success');
    } catch (error) {
      errorManagerRef.current.showError(t('duplication.error.duplicate'));
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleConfirmDuplicateWaba = async () => {
    setIsDuplicating(true);
    try {
      const formData = new FormData();
      formData.append('trigger_id', selectedTriggerId);
      formData.append('entry_tag', selectedTemplate);
      formData.append('schedule_datetime', duplicateDateTime);
      
      const response = await fetch(`${process.env.REACT_APP_URL_DISP}/trigger/duplicate/${acontID}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
        },
        body: formData,
      });

      if (response.ok) {
        setDuplicateWabaModalOpen(false);
        setSelectedTriggerId(null);
        setDuplicateDateTime('');
        setSelectedTemplate('');
        setWabaTemplates([]);
        fetchData(currentPage);
        errorManagerRef.current.showError(t('duplication.success.duplicated'), 'success');
      } else {
        throw new Error('Erro ao duplicar campanha');
      }
    } catch (error) {
      errorManagerRef.current?.showError(t('duplication.error.duplicate'));
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleConfirmDuplicateBroker = async () => {
    setIsDuplicating(true);
    try {
      const formData = new FormData();
      formData.append('trigger_id', selectedTriggerId);
      formData.append('schedule_datetime', duplicateDateTime);
      
      if (brokerMensagemRapida) {
        formData.append('quick_message_id', brokerMensagemRapida);
      } else if (brokerMensagem) {
        formData.append('entry_msg', brokerMensagem);
      }
      // Se não especificou nenhuma mensagem, a API usará a mensagem original
      
      // Adicionar imagem apenas se fornecida (para substituir a original)
      if (brokerImagemUrl) {
        formData.append('entry_link_image', brokerImagemUrl);
      }
      
      // Campos opcionais do exemplo
      formData.append('entry_tag', '');
      
      const response = await fetch(`${process.env.REACT_APP_URL_DISP}/trigger/duplicate/${acontID}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
        },
        body: formData,
      });

      if (response.ok) {
        setDuplicateBrokerModalOpen(false);
        setSelectedTriggerId(null);
        setDuplicateDateTime('');
        setBrokerMensagem('');
        setBrokerMensagemRapida('');
        setBrokerMensagensRapidas([]);
        setBrokerImagem(null);
        setBrokerImagemUrl('');
        fetchData(currentPage);
        errorManagerRef.current.showError(t('duplication.success.duplicated'), 'success');
      } else {
        throw new Error('Erro ao duplicar campanha');
      }
    } catch (error) {
      errorManagerRef.current?.showError(t('duplication.error.duplicate'));
    } finally {
      setIsDuplicating(false);
    }
  };

  return {
    // Estados
    duplicateModalOpen,
    duplicateWabaModalOpen,
    duplicateBrokerModalOpen,
    duplicateDateTime,
    setDuplicateDateTime,
    isDuplicating,
    wabaTemplates,
    selectedTemplate,
    setSelectedTemplate,
    // Estados específicos do BROKER
    brokerMensagem,
    setBrokerMensagem,
    brokerMensagemRapida,
    setBrokerMensagemRapida,
    brokerMensagensRapidas,
    brokerImagem,
    setBrokerImagem,
    brokerImagemUrl,
    setBrokerImagemUrl,
    // Estados de paginação
    isLoadingMoreWabaTemplates,
    isLoadingMoreBrokerTemplates,
    // Channel IDs
    wabaChannelId,
    brokerChannelId,
    // Funções
    handleDuplicateClick,
    handleDuplicateModalClose,
    handleConfirmDuplicate,
    handleConfirmDuplicateWaba,
    handleConfirmDuplicateBroker,
    loadMoreWabaTemplates,
    loadMoreBrokerTemplates
  };
};

export default useDuplication;